import React, { useEffect, useState } from "react"
import moment from "moment"
import "moment/locale/fr"
import { Calendar as BigCalendar, momentLocalizer, Views } from "react-big-calendar"
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop"
import "react-big-calendar/lib/css/react-big-calendar.css"
import "react-big-calendar/lib/addons/dragAndDrop/styles.css"
import "./WeeklyRecurringScheduler.css"

moment.locale("fr")
const localizer = momentLocalizer(moment)
const DnDCalendar = withDragAndDrop(BigCalendar)
const BASE_DATE = moment().startOf("isoWeek").toDate()

const messages = {
  week: "Semaine",
  work_week: "Semaine de travail",
  day: "Jour",
  month: "Mois",
  previous: "Précédent",
  next: "Suivant",
  today: "Aujourd’hui",
  agenda: "Agenda",
  date: "Date",
  time: "Heure",
  event: "Événement",
  noEventsInRange: "Aucun événement",
  showMore: (total) => `+ ${total} en plus`,
}

const CustomWeekHeader = ({ label }) => {
  const dayLabel = label.split(" ")[1]
  return <span>{dayLabel.charAt(0).toUpperCase() + dayLabel.slice(1)}</span>
}

export default function WeeklyRecurringScheduler({ initialPlanification, onPlanificationChange }) {
  const [templateEvents, setTemplateEvents] = useState([])
  const [form, setForm] = useState({
    startDate: "",
    endDate: "",
    recurrenceInterval: 1, // toutes les semaines par défaut
  })

  const handleSlotSelect = ({ start, end }) => {
    const newEvents = []

    templateEvents.forEach((evt) => {
      // Si chevauchement ou continuation directe
      if ((start <= evt.end && end >= evt.start) || moment(start).isSame(evt.end) || moment(end).isSame(evt.start)) {
        start = moment.min(moment(start), moment(evt.start)).toDate()
        end = moment.max(moment(end), moment(evt.end)).toDate()
      } else {
        newEvents.push(evt)
      }
    })

    newEvents.push({ title: "Diffusion", start, end })
    setTemplateEvents(newEvents)
  }

  const handleSlotUpdate = ({ event, start, end }) => {
    // Si l'événement est sur plusieurs jours, on ajuste la fin pour qu'elle soit à 23h59 du jour de début
    let newEnd = end
    if (!moment(start).isSame(end, "day")) {
      newEnd = moment(start).set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).toDate()
    }
    const newEvents = templateEvents.map((e) => (e === event ? { ...e, start, end: newEnd } : e))
    setTemplateEvents(newEvents)
  }

  const handleSlotDelete = (event) => {
    if (window.confirm("Supprimer ce créneau ?")) {
      const newEvents = templateEvents.filter((e) => e.start !== event.start || e.end !== event.end)
      setTemplateEvents(newEvents)
    }
  }

  const handleFormUpdate = (event) => {
    const { name, value } = event.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  const fillCalendarWithTemplateEvents = () => {
    const newEvents = []
    for (let day = 0; day < 7; day++) {
      // Pour chaque jour de la semaine, on ajoute un créneau unique de 0h à 23h59
      const start = moment(BASE_DATE).add(day, "days").set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toDate()
      const end = moment(BASE_DATE).add(day, "days").set({ hour: 23, minute: 59, second: 0, millisecond: 0 }).toDate()
      newEvents.push({ title: "Diffusion", start, end })
    }
    setTemplateEvents(newEvents)
  }

  // Initialisation du formulaire et des événements à partir de la planification initiale
  useEffect(() => {
    if (initialPlanification) {
      let newForm = {
        startDate: initialPlanification.startDate || "",
        endDate: initialPlanification.endDate || "",
        recurrenceInterval: initialPlanification.recurrenceInterval || 1,
      }
      setForm(newForm)
      if (initialPlanification.templateEvents && initialPlanification.templateEvents.length > 0) {
        // Convertir les événements en format attendu par le calendrier
        const newEvents = initialPlanification.templateEvents.map((event) => {
          const start = moment(event.start)
          const end = moment(event.end)
          const newStart = moment()
            .startOf("isoWeek")
            .add(start.isoWeekday() - 1, "days")
          newStart.set({ hour: start.hour(), minute: start.minute() })
          const newEnd = moment()
            .startOf("isoWeek")
            .add(end.isoWeekday() - 1, "days")
          newEnd.set({ hour: end.hour(), minute: end.minute() })
          return {
            ...event,
            start: newStart.toDate(),
            end: newEnd.toDate(),
          }
        })
        setTemplateEvents(newEvents)
      } else setTemplateEvents([])
    } else fillCalendarWithTemplateEvents()
  }, [])

  // Mettre à jour les événements en fonction des changements dans le formulaire
  useEffect(() => {
    onPlanificationChange({
      startDate: form.startDate,
      endDate: form.endDate,
      recurrenceInterval: form.recurrenceInterval,
      templateEvents,
    })
  }, [form.startDate, form.endDate, form.recurrenceInterval, JSON.stringify(templateEvents)])

  return (
    <div className="p-4">
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label>Date de début :</label>
            <input
              type="date"
              className="form-control"
              value={form.startDate}
              name="startDate"
              onChange={handleFormUpdate}
            />
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label>Date de fin :</label>
            <input
              type="date"
              className="form-control"
              value={form.endDate}
              name="endDate"
              onChange={handleFormUpdate}
            />
          </div>
        </div>
        <div className="col">
          <div className="form-group">
            <label>Récurrence :</label>
            <select
              className="form-control"
              value={form.recurrenceInterval}
              name="recurrenceInterval"
              onChange={handleFormUpdate}
            >
              <option value={1}>Chaque semaine</option>
              <option value={2}>Toutes les 2 semaines</option>
              <option value={3}>Toutes les 3 semaines</option>
              <option value={4}>Toutes les 4 semaines</option>
            </select>
          </div>
        </div>
      </div>

      <p className="mb-2">Cliquez et glissez sur le calendrier pour définir vos créneaux hebdomadaires types.</p>

      <DnDCalendar
        style={{ height: 400 }}
        localizer={localizer}
        events={templateEvents}
        defaultView={Views.WEEK}
        views={[Views.WEEK]}
        date={BASE_DATE}
        toolbar={false}
        selectable
        resizable
        draggableAccessor={() => true}
        step={60}
        onSelectSlot={handleSlotSelect}
        onSelectEvent={handleSlotDelete}
        onEventDrop={handleSlotUpdate}
        onEventResize={handleSlotUpdate}
        messages={messages}
        components={{
          week: {
            header: CustomWeekHeader,
          },
        }}
      />

      <button
        type="button"
        className="btn btn-primary mt-2 mr-2"
        onClick={() => {
          fillCalendarWithTemplateEvents()
        }}
      >
        Remplir tout
      </button>

      <button
        type="button"
        className="btn btn-danger mt-2"
        onClick={() => {
          setTemplateEvents([])
        }}
      >
        Effacer tout
      </button>
    </div>
  )
}
